import { z } from "zod";
import {
  ACCEPTED_IMAGE_MIME,
  ACCEPTED_VIDEO_MIME,
  MAX_IMAGE_BYTES,
  MAX_VIDEO_BYTES,
  MEDIA_KINDS,
  MEDIA_STATUSES,
} from "../constants.js";

const acceptedMime = [...ACCEPTED_IMAGE_MIME, ...ACCEPTED_VIDEO_MIME] as const;

/**
 * Étape 1 — l'invité déclare le fichier ; l'API crée le MediaItem (status
 * "uploading") et renvoie les URLs présignées (single PUT ou multipart).
 */
export const initUploadSchema = z
  .object({
    kind: z.enum(MEDIA_KINDS),
    contentType: z.enum(acceptedMime),
    bytes: z.number().int().positive(),
    // L'invité s'identifie par nom + email (mémorisés côté navigateur).
    name: z.string().min(1).max(60),
    email: z.string().email().max(254),
    note: z.string().max(280).optional().or(z.literal("")),
  })
  .superRefine((v, ctx) => {
    const isImage = (ACCEPTED_IMAGE_MIME as readonly string[]).includes(v.contentType);
    if (v.kind === "image" && !isImage) {
      ctx.addIssue({ code: "custom", message: "contentType incohérent avec kind=image" });
    }
    if (v.kind === "video" && isImage) {
      ctx.addIssue({ code: "custom", message: "contentType incohérent avec kind=video" });
    }
    const limit = v.kind === "image" ? MAX_IMAGE_BYTES : MAX_VIDEO_BYTES;
    if (v.bytes > limit) {
      ctx.addIssue({ code: "custom", message: `fichier trop volumineux (max ${limit} octets)` });
    }
  });
export type InitUploadInput = z.infer<typeof initUploadSchema>;

/** Étape 2 — l'invité confirme la fin de l'upload ; l'API met en file un job. */
export const completeUploadSchema = z.object({
  // Pour les uploads multipart : liste des ETag par numéro de part.
  parts: z
    .array(z.object({ partNumber: z.number().int().positive(), etag: z.string().min(1) }))
    .optional(),
});
export type CompleteUploadInput = z.infer<typeof completeUploadSchema>;

export const renditionDtoSchema = z.object({
  kind: z.string(),
  url: z.string().url(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
});

export const mediaItemDtoSchema = z.object({
  id: z.string(),
  kind: z.enum(MEDIA_KINDS),
  status: z.enum(MEDIA_STATUSES),
  /** Nom affiché de l'invité (public). */
  pseudo: z.string(),
  /** Email de l'invité — présent uniquement pour le propriétaire de l'événement. */
  email: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.string(),
  durationS: z.number().nullable(),
  renditions: z.array(renditionDtoSchema),
});
export type MediaItemDto = z.infer<typeof mediaItemDtoSchema>;

export const galleryPageSchema = z.object({
  locked: z.boolean(),
  revealAt: z.string().nullable(),
  items: z.array(mediaItemDtoSchema),
  nextCursor: z.string().nullable(),
});
export type GalleryPage = z.infer<typeof galleryPageSchema>;

/* ---- mur des invités (fil + likes) ---- */

export const likeSchema = z.object({ email: z.string().email() });

export const feedItemDtoSchema = mediaItemDtoSchema.extend({
  likeCount: z.number(),
  likedByMe: z.boolean(),
});
export type FeedItemDto = z.infer<typeof feedItemDtoSchema>;

export const feedPageSchema = z.object({
  items: z.array(feedItemDtoSchema),
  nextCursor: z.string().nullable(),
});
export type FeedPage = z.infer<typeof feedPageSchema>;
